import { useState } from "react";
import { Spinner } from "@fluentui/react-components";
import type { NBAItem } from "../types";
import type { UseActivities } from "../../../approvals/hooks/useActivities";
import { ActivityCard, isApprovalActivity } from "../../../approvals/components/ActivityCard";
import type { Activity } from "../../../approvals/types";
import { ActionItem } from "./ActionItem";
import { SkillMessageRow } from "./SkillMessageRow";
import type { SkillMessage } from "../../../notifications/types";
import styles from "./ActionsTabPanel.module.css";

/** Status buckets shared by every row in the list. An approval is pending
 *  until answered, then approved or declined. An NBA action is pending until
 *  ticked, then approved (completed) — nothing declines an NBA action today. */
export type ActionStatus = "pending" | "approved" | "declined";

/** Bucket for an answered approval. Only an explicit reject declines; any
 *  other answer (approve, or an open-ended reply) lets the run continue. */
export function decisionStatus(decision: string): ActionStatus {
  return decision.trim().toLowerCase() === "reject" ? "declined" : "approved";
}

/** What the Actions tab badge shows: cards awaiting the user, open to-dos and
 *  unread skill messages — the same number as the Pending segment inside the tab. */
export function pendingActionCount(
  activities: Pick<UseActivities, "activities">,
  nbaItems: readonly NBAItem[],
  messages: readonly SkillMessage[] = [],
): number {
  return (
    activities.activities.length +
    nbaItems.filter((item) => !item.is_completed).length +
    messages.filter((m) => !m.read).length
  );
}

/** Messages shown before "Show all", so My tasks stays in view on a short pane. */
const MESSAGES_VISIBLE_LIMIT = 3;

const PRIORITY_RANK: Record<SkillMessage["priority"], number> = { urgent: 0, high: 1, normal: 2, low: 2 };

/** Unread first, then urgent and high, then newest. */
export function sortSkillMessages(messages: readonly SkillMessage[]): SkillMessage[] {
  return [...messages].sort((a, b) => {
    if (a.read !== b.read) return a.read ? 1 : -1;
    const byPriority = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    if (byPriority !== 0) return byPriority;
    return (Date.parse(b.createdAtUtc) || 0) - (Date.parse(a.createdAtUtc) || 0);
  });
}

/** A message is never approved or declined: unread it is Pending, and once
 *  opened it shows only in the unfiltered view. */
function messagesUnderFilter(sorted: SkillMessage[], filter: ActionStatus | null): SkillMessage[] {
  if (filter === null) return sorted;
  return filter === "pending" ? sorted.filter((m) => !m.read) : [];
}

const STATUS_TILES: ReadonlyArray<{ readonly id: ActionStatus; readonly label: string }> = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "declined", label: "Declined" },
];

/** One list, two kinds of row: background-skill HITL decisions and NBA items. */
type Row =
  | {
      readonly kind: "approval";
      readonly status: ActionStatus;
      readonly activity: Activity;
      readonly decision?: string;
    }
  | { readonly kind: "action"; readonly status: ActionStatus; readonly action: NBAItem };

interface ActionsTabPanelProps {
  readonly nbaItems: NBAItem[];
  readonly nbaLoading: boolean;
  readonly nbaError: string | null;
  readonly onToggleNbaStatus: (nbaId: string | null) => void;
  readonly onClearAll?: () => void;
  /** The approvals poll, owned by the task pane so the Actions tab badge is
   *  live whichever tab is open (see TaskPane). */
  readonly activities: UseActivities;
  /** Skills' text messages (Notify steps). Opening one starts its chat session. */
  readonly messages?: readonly SkillMessage[];
  readonly onOpenMessage?: (message: SkillMessage) => void;
}

const TASK_PRIORITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

/** Every row the tab can list: pending approvals first (a paused run is waiting
 *  on the user, which outranks a mere recommendation), then decided ones, then
 *  to-dos by priority and confidence. */
function buildRows(activities: UseActivities["activities"], decided: UseActivities["decided"], nbaItems: NBAItem[]): Row[] {
  const sortedActions = [...nbaItems].sort((a, b) => {
    const pa = TASK_PRIORITY_ORDER[a.priority] ?? 3;
    const pb = TASK_PRIORITY_ORDER[b.priority] ?? 3;
    if (pa !== pb) return pa - pb;
    return b.confidence - a.confidence;
  });
  return [
    ...activities.map((activity): Row => ({ kind: "approval", status: "pending", activity })),
    ...decided.map(
      (d): Row => ({
        kind: "approval",
        status: decisionStatus(d.decision.decision),
        activity: d.activity,
        decision: d.decision.decision,
      }),
    ),
    ...sortedActions.map(
      (action): Row => ({
        kind: "action",
        status: action.is_completed ? "approved" : "pending",
        action,
      }),
    ),
  ];
}

/** Three sections (brief §5b/5d/5e): a skill's interactive cards, approval
 *  gates, then the to-do list — each only when it has rows under the filter. */
function buildSections(visible: Row[], filter: ActionStatus | null) {
  const actionCards = visible.filter((r) => r.kind === "approval" && !isApprovalActivity(r.activity));
  const approvalCards = visible.filter((r) => r.kind === "approval" && isApprovalActivity(r.activity));
  const tasks = visible.filter((r) => r.kind === "action");
  const openTasks = tasks.filter((r) => r.status === "pending").length;
  const pendingOnly = filter === "pending";
  return [
    { id: "actions", title: "Recommended actions", count: `${actionCards.length} pending`, rows: actionCards },
    {
      id: "approvals",
      title: pendingOnly ? "Awaiting approval" : "Approvals",
      count: pendingOnly ? `${approvalCards.length} pending` : `${approvalCards.length}`,
      rows: approvalCards,
    },
    { id: "tasks", title: "My tasks", count: `${openTasks} open`, rows: tasks },
  ].filter((section) => section.rows.length > 0);
}

export function ActionsTabPanel({
  nbaItems,
  nbaLoading,
  nbaError,
  onToggleNbaStatus,
  onClearAll,
  activities: activitiesState,
  messages = [],
  onOpenMessage,
}: Readonly<ActionsTabPanelProps>) {
  // The status tiles are the filter: click one to show that bucket, click it
  // again to show everything.
  const [filter, setFilter] = useState<ActionStatus | null>("pending");
  const [showAllMessages, setShowAllMessages] = useState(false);
  const { activities, decided, loading, error, submittingId, submit } = activitiesState;

  const rows = buildRows(activities, decided, nbaItems);

  const sortedMessages = sortSkillMessages(messages);
  const unreadMessages = sortedMessages.filter((m) => !m.read);

  const counts: Record<ActionStatus, number> = { pending: unreadMessages.length, approved: 0, declined: 0 };
  for (const row of rows) counts[row.status] += 1;

  const visible = filter === null ? rows : rows.filter((row) => row.status === filter);
  const visibleMessages = messagesUnderFilter(sortedMessages, filter);
  const shownMessages = showAllMessages ? visibleMessages : visibleMessages.slice(0, MESSAGES_VISIBLE_LIMIT);

  const sections = buildSections(visible, filter);
  const approvalsLoading = loading && activities.length === 0 && decided.length === 0;
  // The to-do scan streams in the background. Its placeholder belongs to My
  // tasks alone, so approvals and messages show the moment they load. Hidden
  // under Approved and Declined, where a loading to-do could not appear anyway.
  const tasksLoading = nbaLoading && filter !== "declined";
  const taskSection =
    sections.find((section) => section.id === "tasks") ??
    (tasksLoading ? { id: "tasks", title: "My tasks", count: "", rows: [] } : null);

  const hiddenMessages = visibleMessages.length - shownMessages.length;
  const messagesSection =
    visibleMessages.length > 0 && onOpenMessage ? (
      <section className={styles.section} aria-label="Messages">
        <div className={styles.sectionHeader}>
          <span className={styles.sectionMarker} aria-hidden="true" />
          <span className={styles.sectionTitle}>Messages</span>
          <span className={unreadMessages.length > 0 ? `${styles.sectionCount} ${styles.sectionCountNew}` : styles.sectionCount}>
            {unreadMessages.length > 0 ? `${unreadMessages.length} new` : `${visibleMessages.length}`}
          </span>
        </div>
        <ul className={`${styles.list} ${styles.taskCard}`}>
          {shownMessages.map((message) => (
            <li key={message.notificationId} className={styles.messageItem}>
              <SkillMessageRow message={message} onOpen={onOpenMessage} />
            </li>
          ))}
          {hiddenMessages > 0 && (
            <li className={styles.messageItem}>
              <button type="button" className={styles.showAll} onClick={() => setShowAllMessages(true)}>
                Show all {visibleMessages.length} messages
              </button>
            </li>
          )}
        </ul>
      </section>
    ) : null;

  const renderSection = (section: (typeof sections)[number]) => (
    <section key={section.id} className={styles.section} aria-label={section.title}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionMarker} aria-hidden="true" />
        <span className={styles.sectionTitle}>{section.title}</span>
        <span className={styles.sectionCount}>{section.count}</span>
      </div>
      <ul className={`${styles.list} ${section.id === "tasks" ? styles.taskCard : ""}`}>
        {section.rows.map((row) =>
          row.kind === "approval" ? (
            <ActivityCard
              key={row.activity.id}
              activity={row.activity}
              submitting={submittingId === row.activity.id}
              decision={row.decision}
              onSubmit={(decision) => void submit(row.activity, decision)}
            />
          ) : (
            <li
              key={row.action.nba_id ?? `${row.action.title}-${row.action.email_id}`}
              className={styles.listItem}
            >
              <ActionItem
                action={row.action}
                onToggle={() => onToggleNbaStatus(row.action.nba_id)}
              />
            </li>
          ),
        )}
        {section.id === "tasks" && tasksLoading && (
          <li>
            <output className={styles.tasksLoading}>
              <Spinner size="extra-tiny" />
              <span>Loading tasks…</span>
            </output>
          </li>
        )}
      </ul>
    </section>
  );

  return (
    <div className={styles.actionsPanel}>
      <div className={styles.filterRow}>
        <fieldset className={styles.segmented} aria-label="Filter actions by status">
          {STATUS_TILES.map((tile) => (
            <button
              key={tile.id}
              type="button"
              className={filter === tile.id ? `${styles.segment} ${styles.segmentActive}` : styles.segment}
              aria-pressed={filter === tile.id}
              onClick={() => setFilter((current) => (current === tile.id ? null : tile.id))}
            >
              <span className={styles.segmentLabel}>{tile.label}</span>
              <span className={styles.segmentCount}>{counts[tile.id]}</span>
            </button>
          ))}
        </fieldset>
      </div>

      {onClearAll && (
        <div className={styles.filterBar}>
          <button
            type="button"
            className={styles.clearAllBtn}
            onClick={onClearAll}
            disabled={nbaItems.length === 0}
          >
            Clear all
          </button>
        </div>
      )}

      {approvalsLoading && (
        <div className={styles.loading}>
          <Spinner size="tiny" label="Loading approvals…" />
        </div>
      )}

      {nbaError && !nbaLoading && <div className={styles.error}>{nbaError}</div>}

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}

      {!nbaLoading && !approvalsLoading && !nbaError && visible.length === 0 && visibleMessages.length === 0 && (
        <output className={styles.empty}>
          {rows.length === 0 && messages.length === 0 ? (
            <>
              <span className={styles.emptyTitle}>All caught up</span>
              <span>Everything looks in order. No actions needed right now.</span>
            </>
          ) : (
            <span>No {filter} actions.</span>
          )}
        </output>
      )}

      {sections.filter((section) => section.id !== "tasks").map(renderSection)}
      {messagesSection}
      {taskSection && renderSection(taskSection)}
    </div>
  );
}
